import React, { useState, useEffect } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import cellEditFactory from "react-bootstrap-table2-editor";
import { Button } from "@mui/material";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import moment from "moment";
import { connect, shallowEqual, useSelector } from "react-redux";
import i18n from "../../../modules/Auth/pages/i18n";
import { useTranslation } from "react-i18next";

function DayLevelPricing({
  dayLevelPricindData,
  setdayLevelTotalTaxAmount,
  setdayLevelPreTaxAmount,
  setdayLevelTotalTax,
  setdayLevelPerNightAmount,
  setisDaylevelPricingSet,
  onCancelClick,
  currency,
  selectedLanguage,
  updatedDayData,
  setupdatedDayData,
  canCheckMinPriceFromRoomType = false,
  roomTypeMinPrice = 0,
}) {
  const { hotel } = useSelector(
    ({ auth }) => ({
      hotel: auth.selectedHotel,
    }),
    shallowEqual
  );

  const { t } = useTranslation();
  const minPriceNumber = Number(roomTypeMinPrice);

  const getAmountValidationError = (newValue) => {
    if (newValue === "" || newValue === null || newValue === undefined) {
      return "";
    }
    if (isNaN(newValue)) {
      return `${t("Price should be numeric")}`;
    }

    if (!canCheckMinPriceFromRoomType) {
      return "";
    }

    const enteredAmount = Number(newValue);
    if (
      Number.isFinite(minPriceNumber) &&
      minPriceNumber > 0 &&
      !Number.isNaN(enteredAmount) &&
      enteredAmount < minPriceNumber
    ) {
      return `Minimum room type price is ${minPriceNumber}.`;
    }
    return "";
  };

  const validateAmountAgainstMinPrice = (newValue) => {
    if (isNaN(newValue)) {
      return {
        valid: false,
        message: `${t("Price should be numeric")}`,
      };
    }
    return true;
  };

  const isBelowRoomTypeMinPrice = (newValue) => {
    if (!canCheckMinPriceFromRoomType) return false;
    const enteredAmount = Number(newValue);
    return (
      Number.isFinite(minPriceNumber) &&
      minPriceNumber > 0 &&
      !Number.isNaN(enteredAmount) &&
      enteredAmount < minPriceNumber
    );
  };

  const AmountEditor = ({ editorProps, value }) => {
    const [inputValue, setInputValue] = useState(value ?? "");
    const errorMessage = getAmountValidationError(inputValue);
    const showError = Boolean(errorMessage);

    useEffect(() => {
      setInputValue(value ?? "");
    }, [value]);

    const handleChange = (newValue) => {
      setInputValue(newValue);
    };

    const handleEditorKeyDown = (event) => {
      // Keep all key handling inside the input, not the table cell editor.
      event.stopPropagation();
      if (event.key === "Enter") {
        event.preventDefault();
        editorProps.onUpdate(inputValue);
      }
    };

    return (
      <div style={{ width: "100%" }}>
        <div style={{ position: "relative", width: "100%" }}>
          <input
            {...editorProps}
            value={inputValue}
            autoFocus
            style={{
              width: "100%",
              boxSizing: "border-box",
              paddingRight: showError ? "1.3rem" : undefined,
              border: showError ? "1px solid #d32f2f" : undefined,
              outline: "none",
            }}
            onKeyDown={handleEditorKeyDown}
            onBlur={() => editorProps.onUpdate(inputValue)}
            onChange={(e) => handleChange(e.target.value)}
          />
          {showError && (
            <ErrorOutline
              style={{
                color: "#d32f2f",
                fontSize: "16px",
                position: "absolute",
                right: "4px",
                top: "50%",
                transform: "translateY(-50%)",
              }}
            />
          )}
        </div>
        {showError && (
          <div style={{ color: "#d32f2f", fontSize: "11px", marginTop: "2px" }}>
            {errorMessage}
          </div>
        )}
      </div>
    );
  };

  const renderAmountEditor = (editorProps, value) => {
    return <AmountEditor editorProps={editorProps} value={value} />;
  };

  useEffect(() => {
    i18n.changeLanguage(selectedLanguage);
  }, [selectedLanguage]);
  const [columns, setcolumns] = useState([
    {
      dataField: "date",
      text: `${t("Date")}`,
      editable: false,
      formatter: (cell, row) => {
        return moment(cell.split(" ")[0], "DD-MM-YYYY").format("MMM Do YYYY");
      },
    },
    {
      dataField: "amountBeforeTax",
      text: `${t("Amount")} (${currency})`,
      editable: true,
      formatter: (cell, row) => {
        return parseFloat(cell).toFixed(2);
      },
      validator: (newValue, row, column) => {
        return validateAmountAgainstMinPrice(newValue);
      },
      editorRenderer: (editorProps, value) =>
        renderAmountEditor(editorProps, value),
      style: (cell, row, rowIndex, colIndex) => {
        return {
          fontWeight: "500",
          textDecoration: "underline",
        };
      },
    },
    {
      dataField: "tax",
      text: `${t("Tax")} (${currency})`,
      editable: false,
      formatter: (cell, row) => {
        return parseFloat(cell).toFixed(2);
      },
    },
    {
      dataField: "amountAfterTax",
      text: `${t("Total Amount")} (${currency})`,
      editable: true,
      formatter: (cell, row) => {
        return parseFloat(cell).toFixed(2);
      },
      style: (cell, row, rowIndex, colIndex) => {
        return {
          fontWeight: "500",
          textDecoration: "underline",
        };
      },
      validator: (newValue, row, column) => {
        return validateAmountAgainstMinPrice(newValue);
      },
      editorRenderer: (editorProps, value) =>
        renderAmountEditor(editorProps, value),
    },
  ]);

  useEffect(() => {
    setcolumns([
      {
        dataField: "date",
        text: "Date",
        editable: false,
        formatter: (cell, row) => {
          return moment(cell.split(" ")[0], "DD-MM-YYYY").format("MMM Do YYYY");
        },
      },
      {
        dataField: "amountBeforeTax",
        text: `${t("Amount")} (${currency})`,
        editable:
          JSON.parse(hotel?.accessControl?.user_feature_map?.EditBookingPrice)
            .read === "YES",
        formatter: (cell, row) => {
          return parseFloat(cell).toFixed(2);
        },
        validator: (newValue, row, column) => {
          return validateAmountAgainstMinPrice(newValue);
        },
        editorRenderer: (editorProps, value) =>
          renderAmountEditor(editorProps, value),
        style: (cell, row, rowIndex, colIndex) => {
          return {
            fontWeight: "500",
            textDecoration: "underline",
          };
        },
      },
      {
        dataField: "tax",
        text: `${t("Tax")} (${currency})`,
        editable: false,
        formatter: (cell, row) => {
          return parseFloat(cell).toFixed(2);
        },
      },
      {
        dataField: "amountAfterTax",
        text: `${t("Total Amount")} (${currency})`,
        editable:
          JSON.parse(hotel?.accessControl?.user_feature_map?.EditBookingPrice)
            .read === "YES",
        formatter: (cell, row) => {
          return parseFloat(cell).toFixed(2);
        },
        style: (cell, row, rowIndex, colIndex) => {
          return {
            fontWeight: "500",
            textDecoration: "underline",
          };
        },
        validator: (newValue, row, column) => {
          return validateAmountAgainstMinPrice(newValue);
        },
        editorRenderer: (editorProps, value) =>
          renderAmountEditor(editorProps, value),
      },
    ]);
  }, [hotel]);

  const getDetailsAmount = () => {
    let totalTax = 0;
    let amountWithTax = 0;
    let amountBeforeTax = 0;
    setupdatedDayData(dayLevelPricindData);
    const data = dayLevelPricindData?.perDayPrice?.map((e) => {
      amountWithTax = parseFloat(amountWithTax) + parseFloat(e.amountAfterTax);
      totalTax = parseFloat(totalTax) + parseFloat(e.tax);
      amountBeforeTax =
        parseFloat(amountBeforeTax) + parseFloat(e.amountBeforeTax);
    });
    setdayLevelTotalTaxAmount(parseFloat(amountWithTax));
    setdayLevelPreTaxAmount(parseFloat(amountBeforeTax));
    setdayLevelTotalTax(parseFloat(totalTax));
    setdayLevelPerNightAmount(
      parseFloat(
        parseFloat(amountBeforeTax) / dayLevelPricindData?.perDayPrice?.length
      )
    );
  };

  const [counter, setcounter] = useState(0);
  const [editIndiaPrices, seteditIndiaPrices] = useState(true);
  useEffect(() => {
    if (counter === 0 && dayLevelPricindData?.perDayPrice.length > 0) {
      seteditIndiaPrices(
        dayLevelPricindData.perDayPrice[0].taxPercent === 0 ? false : true
      );
      setcounter(1);
    }
  }, [dayLevelPricindData]);

  return (
    <div>
      <div
        style={{ marginLeft: "1rem", marginTop: "1rem", marginRight: "1rem" }}
      >
        {" "}
        {dayLevelPricindData &&
          dayLevelPricindData?.perDayPrice?.length > 0 && (
            <BootstrapTable
              bootstrap4
              keyField="timestamp"
              data={dayLevelPricindData?.perDayPrice}
              columns={columns}
              cellEdit={cellEditFactory({
                mode: "click",
                blurToSave: true,
                afterSaveCell: (oldValue, newValue, row, column) => {
                  if (
                    (column?.dataField === "amountBeforeTax" ||
                      column?.dataField === "amountAfterTax") &&
                    isBelowRoomTypeMinPrice(newValue)
                  ) {
                    row[column?.dataField] = parseFloat(oldValue);
                    return;
                  }

                  if (currency !== "INR") {
                    if (column?.dataField === "amountBeforeTax") {
                      row.amountBeforeTax = parseFloat(newValue);
                      row.tax = parseFloat(
                        (parseFloat(row.amountBeforeTax) *
                          parseFloat(row.taxPercent)) /
                          100
                      );
                      row.amountAfterTax = parseFloat(
                        parseFloat(row.amountBeforeTax) + parseFloat(row.tax)
                      );
                    }
                    if (column?.dataField === "amountAfterTax") {
                      row.amountAfterTax = parseFloat(newValue);
                      row.amountBeforeTax = parseFloat(
                        (parseFloat(row.amountAfterTax) * 100) /
                          (100 + parseFloat(row.taxPercent))
                      );

                      row.tax = parseFloat(
                        (parseFloat(row.amountBeforeTax) *
                          parseFloat(row.taxPercent)) /
                          100
                      );
                    }
                  } else {
                    if (column?.dataField === "amountBeforeTax") {
                      row.amountBeforeTax = parseFloat(newValue);
                      const taxPercent =
                        editIndiaPrices === false
                          ? 0
                          : parseFloat(row.amountBeforeTax) <= 7500
                          ? 12
                          : 18;
                      row.tax = parseFloat(
                        (parseFloat(row.amountBeforeTax) *
                          parseFloat(taxPercent)) /
                          100
                      );
                      row.amountAfterTax = parseFloat(
                        parseFloat(row.amountBeforeTax) + parseFloat(row.tax)
                      );
                    }
                    if (column?.dataField === "amountAfterTax") {
                      row.amountAfterTax = parseFloat(newValue);
                      {
                      }
                      if (
                        parseFloat(row.amountAfterTax) >= 8400 &&
                        parseFloat(row.amountAfterTax) <= 8850
                      ) {
                      } else {
                        const taxPercent =
                          editIndiaPrices === false
                            ? 0
                            : parseFloat(row.amountAfterTax) < 8400
                            ? 12
                            : 18;
                        row.amountBeforeTax = parseFloat(
                          (parseFloat(row.amountAfterTax) * 100) /
                            (100 + parseFloat(taxPercent))
                        );

                        row.tax = parseFloat(
                          (parseFloat(row.amountBeforeTax) *
                            parseFloat(taxPercent)) /
                            100
                        );
                      }
                    }
                    if (column?.dataField === "amountAfterTax") {
                      row.amountAfterTax = parseFloat(newValue);
                      if (
                        parseFloat(row.amountAfterTax) >= 8400 &&
                        parseFloat(row.amountAfterTax) <= 8850
                      ) {
                        row.amountBeforeTax = parseFloat(7500);

                        row.tax = parseFloat(
                          parseFloat(row.amountAfterTax) -
                            parseFloat(row.amountBeforeTax)
                        );
                      }
                    }
                  }
                },
              })}
            />
          )}
      </div>
      <div style={{ float: "right", marginRight: "2rem" }}>
        <Button
          variant="outlined"
          onClick={() => {
            setisDaylevelPricingSet(false);
            setupdatedDayData({});
            onCancelClick();
          }}
          style={{
            marginRight: "1rem",
            color: `${"#36454F"}`,
            borderColor: "#36454F",
            fontWeight: "bold",
            textTransform: "none",
          }}
        >
          {t("Cancel")}
        </Button>
        <Button
          variant="outlined"
          onClick={() => {
            getDetailsAmount();
            setisDaylevelPricingSet(true);
            onCancelClick();
          }}
          style={{
            backgroundColor: `${"#36454F"}`,
            color: `${"white"}`,
            borderColor: "#36454F",
            fontWeight: "bold",
            textTransform: "none",
          }}
        >
          {t("Save")}
        </Button>
      </div>
    </div>
  );
}

export default DayLevelPricing;
